import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import PageShell from "@/components/PageShell";
import Seo from "@/components/Seo";
import { Check, Play } from "lucide-react";
import { HERO_VARIANTS, EXPERIMENT_KEY, pickVariant, getSessionId } from "@/lib/heroExperiment";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_SITE_URL || "https://nematv.com";

const regions = [
  { name: "India",      tagline: "Cinema · Drama · Cricket",  img: "https://images.unsplash.com/photo-1597836228657-d61e739df51b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", stripe: ["#FF9933", "#FFFFFF", "#138808"] },
  { name: "Pakistan",   tagline: "Serials · Sport · Culture", img: "https://images.unsplash.com/photo-1531213203257-16afb0eac95e?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", stripe: ["#01411C", "#01411C", "#FFFFFF"] },
  { name: "Bangladesh", tagline: "News · Drama · Music",      img: "https://images.unsplash.com/photo-1655239709468-d98d317d226c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", stripe: ["#006A4E", "#F42A41", "#006A4E"] },
  { name: "Nepal",      tagline: "News · Culture · Music",    img: "https://images.unsplash.com/photo-1526772662000-3f88f10405ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", stripe: ["#DC143C", "#003893", "#DC143C"] },
];

const includes = [
  "Live channels from India, Pakistan, Bangladesh & Nepal",
  "3-day Cloud DVR on selected channels",
  "HD streaming with adaptive bitrate",
  "Roku · Fire TV · Android · iOS · Web",
  "Cancel anytime, no lock-in",
];

const Paisley = () => (
  <svg viewBox="0 0 200 40" className="w-full h-6 opacity-30" aria-hidden="true">
    <defs>
      <linearGradient id="pg" x1="0" x2="1">
        <stop offset="0" stopColor="#FF7A1A" />
        <stop offset="0.5" stopColor="#F43F5E" />
        <stop offset="1" stopColor="#FFB800" />
      </linearGradient>
    </defs>
    <g fill="none" stroke="url(#pg)" strokeWidth="1">
      {Array.from({ length: 10 }).map((_, i) => (
        <path key={i} d={`M${i * 20},20 q5,-10 10,0 t10,0`} />
      ))}
    </g>
  </svg>
);

const trackEvent = async (variantId, event) => {
  try {
    await axios.post(`${API}/experiments/track`, {
      experiment: EXPERIMENT_KEY,
      variant: variantId,
      event,
      session_id: getSessionId(),
    });
  } catch {
    /* non-blocking */
  }
};

const GREETINGS = [
  { script: "नमस्ते",         roman: "Namaste",    lang: "hi", flag: "India · Nepal" },
  { script: "স্বাগতম",         roman: "Shagotom",   lang: "bn", flag: "Bangladesh" },
  { script: "السلام علیکم",   roman: "Salaam",     lang: "ur", flag: "Pakistan",  rtl: true },
];

const RotatingGreeting = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % GREETINGS.length), 3200);
    return () => clearInterval(t);
  }, []);
  const g = GREETINGS[i];
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.15 }}
      data-testid="desi-hero-namaste"
      className="mt-5 mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 min-h-[2.75rem] md:min-h-[3rem]"
    >
      <motion.span
        key={g.script}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        lang={g.lang}
        dir={g.rtl ? "rtl" : "ltr"}
        data-testid={`desi-greeting-${g.lang}`}
        className="font-display font-extrabold text-3xl md:text-4xl leading-none"
        style={{
          backgroundImage: "linear-gradient(90deg, #FFB800 0%, #FF7A1A 60%, #F43F5E 100%)",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
        }}
      >
        {g.script}
      </motion.span>
      <span className="text-white/70 text-sm md:text-base tracking-wide">
        <span className="text-white/90 font-semibold">{g.roman}</span>
        {" — welcome home, wherever home is."}
      </span>
    </motion.div>
  );
};

const DesiTVPage = () => {
  const [variant, setVariant] = useState(() => HERO_VARIANTS[0]);
  const [videoOk, setVideoOk] = useState(true);
  const viewedRef = useRef(false);
  const convertedRef = useRef(false);

  useEffect(() => {
    const v = pickVariant();
    setVariant(v);
    if (!viewedRef.current) {
      viewedRef.current = true;
      trackEvent(v.id, "view");
    }
  }, []);

  const trackConversion = () => {
    if (convertedRef.current) return;
    convertedRef.current = true;
    trackEvent(variant.id, "conversion");
  };

  return (
    <PageShell className="!pt-0">
      <Seo
        title="Desi TV Package — Live Indian, Pakistani, Bangladeshi & Nepali Channels | Nema TV"
        description="Watch live Indian, Pakistani, Bangladeshi and Nepali TV on Nema TV. Cricket, cinema, news and prime-time drama on Roku, Fire TV, Android, iOS and Web. Plans from $14.99 — cancel anytime."
        keywords="desi tv package, live indian tv, pakistani channels streaming, bangla tv online, nepali live tv, cricket live streaming, bollywood live, south asian iptv"
        path="/packages/desi-tv"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Packages" },
              { "@type": "ListItem", position: 3, name: "Desi TV", item: `${SITE_URL}/packages/desi-tv` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Nema TV — Desi TV Package",
            description: "Live TV from India, Pakistan, Bangladesh and Nepal — cricket, cinema, news and prime-time drama on every screen.",
            brand: { "@type": "Brand", name: "Nema TV" },
            offers: {
              "@type": "Offer",
              priceCurrency: "USD",
              price: "14.99",
              availability: "https://schema.org/InStock",
              url: `${SITE_URL}/packages/desi-tv`,
            },
          },
        ]}
      />

      {/* Full-bleed cinematic hero — A/B rotates between self-hosted variants */}
      <section
        data-testid="desi-hero"
        data-variant={variant.id}
        className="relative min-h-[92svh] flex items-end overflow-hidden isolate"
      >
        <div className="absolute inset-0 z-0">
          {videoOk ? (
            <video
              key={variant.id}
              data-testid="desi-hero-video"
              src={variant.video}
              poster={variant.poster}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-hidden="true"
              onError={() => setVideoOk(false)}
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={variant.poster}
              alt=""
              aria-hidden="true"
              data-testid="desi-hero-poster-fallback"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 20% 30%, rgba(255,138,0,0.35) 0%, transparent 55%), radial-gradient(ellipse at 80% 70%, rgba(244,63,94,0.35) 0%, transparent 55%)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0503] via-[#0a0503]/70 to-[#0a0503]/40" />
          <div className="absolute inset-0 bg-[#0a0503]/35" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24 pt-32 md:pt-40 w-full">
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75 }}
            className="flex items-center gap-2 mb-4"
          >
            <span className="w-10 h-px bg-[#FFB800]" />
            <span className="text-[11px] uppercase tracking-[0.32em] text-[#FFB800] font-semibold">
              Desi TV Package · Live now
            </span>
          </motion.div>

          <RotatingGreeting />

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.05 }}
            className="font-display font-extrabold leading-[0.92] text-5xl md:text-7xl xl:text-[104px] tracking-tighter max-w-5xl"
          >
            <span className="text-white">Ghar ki TV,</span>
            <br />
            <span
              style={{
                backgroundImage: "linear-gradient(90deg, #FF7A1A 0%, #F43F5E 45%, #FFB800 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              wherever you are.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-7 text-lg md:text-xl text-white/85 max-w-2xl"
          >
            Live channels from <b className="text-white">India · Pakistan · Bangladesh · Nepal</b> —
            cricket, cinema, evening news and prime-time drama, streaming in cinematic
            quality on every screen you own.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.32 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <a
              href="https://tv.nematv.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackConversion}
              data-testid="desi-cta-subscribe"
              className="inline-flex items-center gap-2 rounded-full text-[#0a0503] px-8 py-4 text-base font-bold shadow-[0_0_45px_rgba(255,138,0,0.4)] transition-transform hover:scale-[1.02]"
              style={{ background: "linear-gradient(90deg, #FFB800 0%, #FF7A1A 50%, #F43F5E 100%)" }}
            >
              <Play className="w-4 h-4" /> Start Watching — $14.99
            </a>
            <Link
              to="/#pricing"
              onClick={trackConversion}
              data-testid="desi-cta-plans"
              className="inline-flex items-center rounded-full bg-white/5 border border-white/25 backdrop-blur px-8 py-4 text-base font-medium text-white hover:bg-white/10 transition-colors"
            >
              See all plans
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-10 flex flex-wrap gap-2.5"
          >
            {["Cricket", "Cinema", "Prime-time Drama", "News", "Music", "Kids"].map((c) => (
              <span key={c} className="text-xs md:text-sm rounded-full border border-[#FFB800]/25 bg-black/30 backdrop-blur px-4 py-1.5 text-[#FFE8B3]">
                {c}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <Paisley />
      </div>

      {/* Four countries showcase */}
      <section className="relative pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#FFB800] font-semibold mb-2">Four homelands</div>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold leading-tight">
                One remote,{" "}
                <span style={{ backgroundImage: "linear-gradient(90deg,#FF7A1A,#F43F5E)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>four homelands.</span>
              </h2>
            </div>
            <Link to="/#regions" className="text-sm text-[#FFB800] font-semibold hover:underline">See all live →</Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {regions.map((r, i) => (
              <motion.div
                key={r.name}
                data-testid={`desi-region-${r.name.toLowerCase()}`}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="group relative rounded-3xl overflow-hidden border border-white/10 bg-[#120a06] hover:border-[#FFB800]/40 transition-colors"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={r.img} alt={r.name} loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[1500ms] ease-out" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0503]/95 via-[#0a0503]/30 to-transparent" />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-black/45 border border-white/15 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-white">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                    Live
                  </div>
                  <div className="absolute bottom-0 inset-x-0 p-5">
                    <div className="font-display text-3xl md:text-4xl font-extrabold leading-none text-white">{r.name}</div>
                    <div className="mt-2 text-sm text-white/70">{r.tagline}</div>
                  </div>
                </div>
                <div className="flex h-1.5">
                  {r.stripe.map((c, idx) => (
                    <div key={idx} className="flex-1" style={{ background: c }} />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Included + pricing card */}
      <section className="relative py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-5 gap-8 items-stretch">
          <div className="lg:col-span-3 rounded-3xl border border-white/10 bg-gradient-to-br from-[#1a0d06] to-[#0a0503] p-8 md:p-10">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#FFB800] font-semibold mb-3">What&apos;s included</div>
            <h3 className="font-display text-3xl md:text-4xl font-extrabold leading-tight">
              Everything in the <span style={{ backgroundImage: "linear-gradient(90deg,#FF7A1A,#F43F5E)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Desi TV Package.</span>
            </h3>
            <ul className="mt-7 space-y-3.5 text-white/85">
              {includes.map((it) => (
                <li key={it} className="flex items-start gap-3">
                  <span className="mt-1 w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg,#FFB800,#F43F5E)" }}>
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </span>
                  {it}
                </li>
              ))}
            </ul>
          </div>

          <div
            className="lg:col-span-2 relative overflow-hidden rounded-3xl p-8 md:p-10 flex flex-col"
            style={{ background: "linear-gradient(160deg, #F43F5E 0%, #FF7A1A 55%, #FFB800 100%)" }}
          >
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/25 blur-3xl" />
            <div className="relative flex-1 flex flex-col">
              <div className="text-white/85 text-sm uppercase tracking-[0.2em] font-semibold mb-2">Start from</div>
              <div className="font-display text-6xl md:text-7xl font-extrabold text-white leading-none">$14.99</div>
              <div className="text-white/85 mt-2">per month · 12-month plan drops to ~$9.58/mo</div>
              <p className="text-white/85 text-sm mt-4">
                Same channels, same devices — the longer you stay, the less you pay per month.
              </p>
              <div className="mt-auto pt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/#pricing" onClick={trackConversion} className="inline-flex items-center justify-center rounded-full bg-[#0a0503] text-white px-6 py-3 text-sm font-semibold hover:bg-[#1a0d06] transition-colors">
                  See all plans
                </Link>
                <a href="https://tv.nematv.com" target="_blank" rel="noopener noreferrer" onClick={trackConversion} className="inline-flex items-center justify-center rounded-full bg-white text-[#0a0503] px-6 py-3 text-sm font-semibold hover:bg-white/90 transition-colors">
                  Subscribe now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default DesiTVPage;
